import { fromJS } from "immutable";
import ACTIONS from "../constants";
import {
  enrichShiftScheduleTotals,
  enrichShiftScheduleTotalsForCalendarRow,
  getPaginationObj,
  parseBooleanVal,
} from "Utils/utils";
import {
  getAdvanceShiftCalendarTargetIds,
  isAdvanceMultiAssignmentShift,
} from "pages/Rostering/Calendar/advanceShiftCalendarUtils";
import {
  TIMESHEET_STATUS,
  PAYROLL_EXPORT_STATUS,
  emptyPaginationObj,
} from "Constants/constant";
import _ from "lodash";

/** Worker / date / participant — excludes status & page so filter refetch keeps stale rows. */
const timesheetListFetchContextKey = (params) => {
  if (!params) return "";
  const workerId = params.worker_id;
  const workerKey =
    workerId === "all" ? "all" : JSON.stringify(workerId ?? "");
  const range = params.timesheet_date_range;
  const rangeKey =
    Array.isArray(range) && range.length >= 2
      ? `${range[0]}-${range[1]}`
      : "";
  const ptId = params.pt_id;
  const ptKey =
    ptId === "all" || ptId == null ? "all" : JSON.stringify(ptId);
  return `${workerKey}|${rangeKey}|${ptKey}`;
};

const initialState = fromJS({
  workerAndParticipantList: null,
  isSortCalendarShiftsItem: 0,
  isCalendarStartOfDate: null,
  calendarView: null,
  isAuthenticateForPayroll: null,
  showRecurringModal: false,
  rosterInvoices: {},
  shiftRequests: {
    list: [],
    loading: false,
    error: null,
    updatingStatus: false,
    statusUpdateSuccess: false,
  },
  pendingShiftRequestApprovalUuid: null,
});

export default function rosteringReducer(state = initialState, action) {
  switch (action.type) {
    case ACTIONS.FETCH_WORKER_AND_PARTICIPANT_LIST.PENDING: {
      const calView = state.get("calendarView") || null;

      if (calView && calView !== action.calendarView) {
        return state
          .set("getWorkerAndPartsListLoader", true)
          .set("getWorkerAndPartsListError", "")
          .set("calendarView", action.calendarView)
          .set("workerAndParticipantList", null)
          .set(
            "searchtextOfScheduleShifts",
            action.hasOwnProperty("search") ? action.search : null
          );
      }

      return state
        .set(
          "searchtextOfScheduleShifts",
          action.hasOwnProperty("search") ? action.search : null
        )
        .set("getWorkerAndPartsListLoader", true)
        .set("getWorkerAndPartsListError", "")
        .set("calendarView", action.calendarView);
    }
    case ACTIONS.FETCH_WORKER_AND_PARTICIPANT_LIST.SUCCESS: {
      const calView = state.get("calendarView");
      const prevData = action.isRefreshCall
        ? null
        : state.get("workerAndParticipantList") || null;
      const newData = action.data.worker_participant_listing;

      const paginationObj = calView
        ? { ...getPaginationObj(newData?.[`${calView}`]) }
        : { ...emptyPaginationObj };

      if (
        calView &&
        prevData &&
        prevData[`${calView}`] &&
        newData &&
        newData[`${calView}`]
      ) {
        prevData[`${calView}`].data.push(...newData[`${calView}`].data);
        Object.assign(prevData, {
          ...paginationObj,
        });
      }

      const finalData = {};

      if (calView === "worker") {
        Object.assign(finalData, {
          worker: prevData !== null ? prevData?.worker : newData?.worker,
        });
      } else {
        Object.assign(finalData, {
          participant:
            prevData !== null ? prevData?.participant : newData?.participant,
        });
      }

      Object.assign(finalData, {
        ...getPaginationObj(newData[`${calView}`]),
        advance_drafts: Array.isArray(newData?.advance_drafts)
          ? newData.advance_drafts
          : prevData?.advance_drafts || [],
      });

      return state
        .set("getWorkerAndPartsListLoader", false)
        .set("getWorkerAndPartsListError", "")
        .set("workerAndParticipantList", { ...finalData });
    }
    case ACTIONS.FETCH_WORKER_AND_PARTICIPANT_LIST.ERROR: {
      return state
        .set("getWorkerAndPartsListLoader", false)
        .set("getWorkerAndPartsListError", "");
    }

    case ACTIONS.FETCH_SHIFT_REQUESTS.PENDING: {
      return state
        .setIn(["shiftRequests", "loading"], true)
        .setIn(["shiftRequests", "error"], null);
    }
    case ACTIONS.FETCH_SHIFT_REQUESTS.SUCCESS: {
      const list = Array.isArray(action.payload) ? action.payload : [];
      return state
        .setIn(["shiftRequests", "list"], list)
        .setIn(["shiftRequests", "loading"], false)
        .setIn(["shiftRequests", "error"], null);
    }
    case ACTIONS.FETCH_SHIFT_REQUESTS.ERROR: {
      return state
        .setIn(["shiftRequests", "list"], [])
        .setIn(["shiftRequests", "loading"], false)
        .setIn(["shiftRequests", "error"], action.payload);
    }

    case ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.PENDING: {
      return state
        .setIn(["shiftRequests", "updatingStatus"], true)
        .setIn(["shiftRequests", "statusUpdateSuccess"], false);
    }
    case ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.SUCCESS: {
      return state
        .setIn(["shiftRequests", "updatingStatus"], false)
        .setIn(["shiftRequests", "statusUpdateSuccess"], true);
    }
    case ACTIONS.UPDATE_SHIFT_REQUEST_STATUS.ERROR: {
      return state
        .setIn(["shiftRequests", "updatingStatus"], false)
        .setIn(["shiftRequests", "statusUpdateSuccess"], false);
    }

    case ACTIONS.SET_PENDING_SHIFT_REQUEST_APPROVAL: {
      return state.set("pendingShiftRequestApprovalUuid", action.payload);
    }

    case ACTIONS.CLEAR_PENDING_SHIFT_REQUEST_APPROVAL: {
      return state.set("pendingShiftRequestApprovalUuid", null);
    }

    case ACTIONS.SORT_CALENDAR_SHIFTS_ITEM: {
      const isSort = state.get("isSortCalendarShiftsItem");
      return state.set("isSortCalendarShiftsItem", isSort === 0 ? 1 : 0);
    }

    case ACTIONS.CALENDAR_DATE_CHANGE_LOCALLY: {
      return state
        .set("isCalendarStartOfDate", action.data.date)
        .set("scheduleCalendarView", action.data.view);
    }

    case ACTIONS.CREATE_OR_UPDATE_WORKER_SHIFT.PENDING: {
      return state
        .set("createOrUpdateWorkerShiftLoader", true)
        .set("createOrUpdateWorkerShiftError", "");
    }
    case ACTIONS.CREATE_OR_UPDATE_WORKER_SHIFT.SUCCESS: {
      const prevWorkerAndParticipantList =
        state.get("workerAndParticipantList") || null;
      const targetKey =
        action.calendarView === "worker-view" ? "worker" : "participant";

      // Normalize response data to always be an array
      const responseData = Array.isArray(action.data)
        ? action.data
        : action.data
          ? [action.data]
          : [];

      if (
        prevWorkerAndParticipantList &&
        responseData &&
        responseData.length > 0
      ) {
        // Replacing one shift (e.g. open shift) with several new rows (e.g. group
        // shift) returns new UUIDs from the API; remove the edited shift UUID so
        // it does not stay on the calendar until a full refresh.
        if (
          action.isUpdate &&
          action.previousShiftUuid &&
          prevWorkerAndParticipantList[`${targetKey}`]?.data?.length > 0
        ) {
          prevWorkerAndParticipantList[`${targetKey}`].data.forEach(
            (entity) => {
              if (!entity.shifts?.length) return;
              for (let s = entity.shifts.length - 1; s >= 0; s--) {
                if (entity.shifts[s].uuid === action.previousShiftUuid) {
                  entity.shifts.splice(s, 1);
                }
              }
            }
          );
        }

        for (let i = 0; i < responseData.length; i++) {
          const createdShift = responseData[i];
          const enrichedShift = parseBooleanVal(createdShift?.is_multi_assignment)
            ? createdShift
            : enrichShiftScheduleTotals(createdShift);
          let targetIds = [];
          const advanceTargetIds = getAdvanceShiftCalendarTargetIds(
            createdShift,
            targetKey
          );

          if (advanceTargetIds !== null) {
            targetIds = advanceTargetIds;
          } else if (targetKey === "worker") {
            let workerId = action?.isUpdate
              ? enrichedShift?.worker?.id
              : enrichedShift?.worker_id;
            if (workerId) targetIds.push(workerId);
          } else {
            if (
              enrichedShift?.participants &&
              enrichedShift?.participants.length > 0
            ) {
              targetIds = enrichedShift?.participants.map((p) => p.id);
            } else if (enrichedShift?.pt_id) {
              targetIds.push(enrichedShift?.pt_id);
            }
          }

          if (
            action.calendarView &&
            prevWorkerAndParticipantList[`${targetKey}`] &&
            prevWorkerAndParticipantList[`${targetKey}`]?.data.length > 0
          ) {
            targetIds.forEach((targetId) => {
              const rowShift = parseBooleanVal(createdShift?.is_multi_assignment)
                ? enrichShiftScheduleTotalsForCalendarRow(
                    createdShift,
                    targetKey,
                    targetId
                  )
                : enrichedShift;
              const index = prevWorkerAndParticipantList[
                `${targetKey}`
              ].data.findIndex((x) => x.id === targetId);

              if (
                index !== -1 &&
                prevWorkerAndParticipantList[`${targetKey}`].data[index]?.shifts
              ) {
                if (action.isUpdate) {
                  const shiftIndex = prevWorkerAndParticipantList[
                    `${targetKey}`
                  ].data[index].shifts.findIndex(
                    (shift) => shift.uuid === rowShift.uuid
                  );

                  if (shiftIndex !== -1) {
                    // Replace the entire shift object with the updated data to ensure all fields are refreshed
                    prevWorkerAndParticipantList[`${targetKey}`].data[
                      index
                    ].shifts.splice(shiftIndex, 1, rowShift);
                  } else {
                    prevWorkerAndParticipantList[`${targetKey}`].data[
                      index
                    ].shifts.push(rowShift);
                  }
                } else {
                  prevWorkerAndParticipantList[`${targetKey}`].data[
                    index
                  ].shifts.push(rowShift);
                }
              }
            });

            // Cleanup: remove the shift from any participant/worker who is NO LONGER assigned to it
            if (action.isUpdate) {
              prevWorkerAndParticipantList[`${targetKey}`].data.forEach((entity, entityIndex) => {
                if (!targetIds.includes(entity.id) && entity.shifts) {
                  const shiftIndex = entity.shifts.findIndex(
                    (shift) => shift.uuid === enrichedShift.uuid
                  );
                  if (shiftIndex !== -1) {
                    prevWorkerAndParticipantList[`${targetKey}`].data[entityIndex].shifts.splice(shiftIndex, 1);
                  }
                }
              });
            }
          }
        }
      }
      return state
        .set("createOrUpdateWorkerShiftLoader", false)
        .set("createOrUpdateWorkerShiftError", "")
        .set("workerAndParticipantList", { ...prevWorkerAndParticipantList })
        .set("recurringMessage", action.recurring);
    }
    case ACTIONS.CREATE_OR_UPDATE_WORKER_SHIFT.ERROR: {
      return state
        .set("createOrUpdateWorkerShiftLoader", false)
        .set("createOrUpdateWorkerShiftError", action.error);
    }

    case ACTIONS.WORKER_AND_PARTICIPANT_SHIFT_DELETE_LOCALLY: {
      const prevWorkerAndParticipantList =
        state.get("workerAndParticipantList") || null;
      if (prevWorkerAndParticipantList) {
        if (
          action.data.calendarView &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`] &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`]?.data &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`].data
            .length > 0
        ) {
          const index = prevWorkerAndParticipantList[
            `${action.data.calendarView}`
          ].data.findIndex((x) => x.id === action.data.userId);
          if (
            index !== -1 &&
            prevWorkerAndParticipantList[`${action.data.calendarView}`].data[
              index
            ]?.shifts
          ) {
            if (action.data.shiftUuid) {
              const shiftIndex = prevWorkerAndParticipantList[
                `${action.data.calendarView}`
              ].data[index].shifts.findIndex(
                (shift) => shift.uuid === action.data.shiftUuid
              );
              if (shiftIndex !== -1) {
                prevWorkerAndParticipantList[
                  `${action.data.calendarView}`
                ].data[index].shifts.splice(shiftIndex, 1);
              }
            }
          }
        }
      }
      return state.set("workerAndParticipantList", {
        ...prevWorkerAndParticipantList,
      });
    }

    case ACTIONS.ADVANCE_SHIFT_DRAFT_DELETE_LOCALLY: {
      const prevList = state.get("workerAndParticipantList") || null;
      if (!prevList) return state;
      const draftUuid = action.data?.uuid;
      if (!draftUuid) return state;
      const nextDrafts = (prevList.advance_drafts || []).filter(
        (draft) => draft?.uuid !== draftUuid
      );
      return state.set("workerAndParticipantList", {
        ...prevList,
        advance_drafts: nextDrafts,
      });
    }

    case ACTIONS.ADVANCE_SHIFT_DRAFT_UPSERT_LOCALLY: {
      const prevList = state.get("workerAndParticipantList") || null;
      const draft = action.data?.draft;
      if (!draft?.uuid) return state;
      const existing = Array.isArray(prevList?.advance_drafts)
        ? prevList.advance_drafts
        : [];
      const index = existing.findIndex((item) => item?.uuid === draft.uuid);
      const nextDrafts =
        index === -1
          ? [...existing, draft]
          : existing.map((item, i) =>
              i === index ? { ...item, ...draft } : item
            );
      return state.set("workerAndParticipantList", {
        ...(prevList || {}),
        advance_drafts: nextDrafts,
      });
    }

    case ACTIONS.WORKER_AND_PARTICIPANT_SHIFTS_BULK_DELETE_LOCALLY: {
      const prevWorkerAndParticipantList =
        state.get("workerAndParticipantList") || null;
      if (prevWorkerAndParticipantList && action.data.shifts && Array.isArray(action.data.shifts)) {
        const calendarView = action.data.calendarView;
        if (
          calendarView &&
          prevWorkerAndParticipantList[calendarView] &&
          prevWorkerAndParticipantList[calendarView]?.data
        ) {
          // Group shifts by userId for efficient batch processing
          const shiftsByUserId = {};
          action.data.shifts.forEach((shiftData) => {
            const userId = shiftData.userId;
            if (!shiftsByUserId[userId]) {
              shiftsByUserId[userId] = [];
            }
            shiftsByUserId[userId].push(shiftData.shiftUuid);
          });

          // Process each user's shifts in one go
          Object.keys(shiftsByUserId).forEach((userId) => {
            const userIndex = prevWorkerAndParticipantList[calendarView].data.findIndex(
              (x) => x.id === parseInt(userId)
            );
            if (
              userIndex !== -1 &&
              prevWorkerAndParticipantList[calendarView].data[userIndex]?.shifts
            ) {
              const shiftUuids = shiftsByUserId[userId];
              // Remove all shifts for this user in one operation
              prevWorkerAndParticipantList[calendarView].data[userIndex].shifts =
                prevWorkerAndParticipantList[calendarView].data[userIndex].shifts.filter(
                  (shift) => !shiftUuids.includes(shift.uuid)
                );
            }
          });
        }
      }
      return state.set("workerAndParticipantList", {
        ...prevWorkerAndParticipantList,
      });
    }

    case ACTIONS.WORKER_AND_PARTICIPANT_SHIFT_STATUS_CHANGED_LOCALLY: {
      const prevWorkerAndParticipantList =
        state.get("workerAndParticipantList") || null;
      if (prevWorkerAndParticipantList) {
        if (
          action.data.calendarView &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`] &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`].data &&
          prevWorkerAndParticipantList[`${action.data.calendarView}`].data
            .length > 0
        ) {
          const shifts = action.data.shifts || [];
          const viewKey = `${action.data.calendarView}`;
          for (let i = 0; i < shifts.length; i++) {
            // Advance (multi-assignment): update every calendar row that holds
            // this uuid. Legacy path below is unchanged.
            if (isAdvanceMultiAssignmentShift(shifts[i])) {
              const advanceUuid = shifts[i]?.uuid;
              if (!advanceUuid) continue;

              const nextStatus = shifts[i]?.status || action.data.status;
              prevWorkerAndParticipantList[viewKey].data.forEach((entity) => {
                if (!entity?.shifts?.length) return;
                const shiftIndex = entity.shifts.findIndex(
                  (shift) => shift.uuid === advanceUuid
                );
                if (shiftIndex === -1) return;

                const prevShift = entity.shifts[shiftIndex];
                const updatedShift = {
                  ...prevShift,
                  status: nextStatus,
                };
                if (shifts[i]?.progress != null) {
                  updatedShift.progress = shifts[i].progress;
                }
                if (shifts[i]?.shift_accept != null) {
                  updatedShift.shift_accept = shifts[i].shift_accept;
                }
                if (Array.isArray(shifts[i]?.workers)) {
                  updatedShift.workers = shifts[i].workers;
                }
                // New array ref so calendar rows re-render without a full refresh.
                const nextShifts = entity.shifts.slice();
                nextShifts[shiftIndex] = updatedShift;
                entity.shifts = nextShifts;
              });
              continue;
            }

            const compareIdBaseOnCalender =
              action.data.calendarView === "worker"
                ? shifts[i]?.["worker_id"]
                : shifts[i]?.participants?.length > 0
                  ? shifts[i]?.participants[0]?.id
                  : shifts[i]?.["pt_id"];
            const userIndex = prevWorkerAndParticipantList[viewKey].data.findIndex(
              (x) => x.id === compareIdBaseOnCalender
            );
            if (
              userIndex !== -1 &&
              prevWorkerAndParticipantList[viewKey].data[userIndex]?.shifts
            ) {
              const shiftIndex = prevWorkerAndParticipantList[viewKey].data[
                userIndex
              ].shifts.findIndex((shift) => shift.uuid === shifts[i]?.uuid);
              if (shiftIndex !== -1) {
                const prevShift =
                  prevWorkerAndParticipantList[viewKey].data[userIndex].shifts[
                    shiftIndex
                  ];
                prevWorkerAndParticipantList[viewKey].data[
                  userIndex
                ].shifts.splice(shiftIndex, 1, {
                  ...prevShift,
                  status: action.data.status,
                });
              }
            }
          }
        }
      }
      return state.set("workerAndParticipantList", {
        ...prevWorkerAndParticipantList,
      });
    }

    case ACTIONS.WORKER_AND_PARTICIPANT_SHFIT_COPY.PENDING: {
      return state
        .set("copyShiftLoader", action.data || "")
        .set("copyShiftError", "");
    }
    case ACTIONS.WORKER_AND_PARTICIPANT_SHFIT_COPY.SUCCESS: {
      const prevWorkerAndParticipantList =
        state.get("workerAndParticipantList") || null;
      const targetKey =
        action.calendarView === "worker-view"
          ? "worker"
          : action.calendarView === "participant-view"
            ? "participant"
            : null;
      const rows = targetKey
        ? prevWorkerAndParticipantList?.[`${targetKey}`]?.data
        : null;

      if (action.data && Array.isArray(rows) && rows.length > 0) {
        const sourceShift = action.data;
        // Advance (multi-assignment) paste clones the whole shift, so the copy
        // lands on every assigned worker / participant row — same as the
        // calendar listing attaches it after a refresh. Legacy copy re-points
        // the new shift at the pasted row, so its single-target logic stays.
        const advanceTargetIds = getAdvanceShiftCalendarTargetIds(
          sourceShift,
          targetKey
        );
        let targetIds = [];

        if (advanceTargetIds !== null) {
          targetIds = advanceTargetIds;
        } else if (targetKey === "worker") {
          if (sourceShift.worker_id) targetIds.push(sourceShift.worker_id);
        } else {
          const pastedParticipantId =
            sourceShift?.participants?.[0]?.id ?? sourceShift?.pt_id;
          if (pastedParticipantId) targetIds.push(pastedParticipantId);
        }

        targetIds.forEach((targetId) => {
          const pastedShift = parseBooleanVal(sourceShift?.is_multi_assignment)
            ? enrichShiftScheduleTotalsForCalendarRow(
                sourceShift,
                targetKey,
                targetId
              )
            : enrichShiftScheduleTotals(sourceShift);
          const index = rows.findIndex(
            (x) => Number(x.id) === Number(targetId)
          );
          if (index === -1 || !Array.isArray(rows[index]?.shifts)) return;
          if (
            rows[index].shifts.some((shift) => shift.uuid === pastedShift.uuid)
          ) {
            return;
          }
          // New array ref so calendar rows re-render without a full refresh.
          rows[index].shifts = [...rows[index].shifts, pastedShift];
        });
      }

      return state
        .set("copyShiftLoader", null)
        .set("copyShiftError", "")
        .set("workerAndParticipantList", { ...prevWorkerAndParticipantList });
    }
    case ACTIONS.WORKER_AND_PARTICIPANT_SHFIT_COPY.ERROR: {
      return state
        .set("copyShiftLoader", null)
        .set("copyShiftError", action.error);
    }

    case ACTIONS.PARTICIPANT_LIST_FOR_TIMESHEET.PENDING: {
      return state
        .set("workerTimeSheetsList", null)
        .set("participantListForTimesheetLoader", true)
        .set("participantListForTimesheetError", "");
    }
    case ACTIONS.PARTICIPANT_LIST_FOR_TIMESHEET.SUCCESS: {
      return state
        .set("participantListForTimesheetLoader", false)
        .set("participantListForTimesheetError", "")
        .set("participantListForTimesheet", [...action.data]);
    }
    case ACTIONS.PARTICIPANT_LIST_FOR_TIMESHEET.ERROR: {
      return state
        .set("participantListForTimesheetLoader", false)
        .set("participantListForTimesheetError", action.error);
    }

    case ACTIONS.WORKER_TIMESHEETS.PENDING: {
      const prevList = state.get("workerTimeSheetsList") || [];
      const PrevSelectedTimesheetItem =
        state.get("selectedTimesheetOfParticipant") || null;

      return state
        .set("workerTimesheetLoader", true)
        .set(
          "workerTimesheetPaginationLoader",
          action.data?.paginationCall || false
        )
        .set("workerTimesheetError", "")
        .set(
          "selectedTimesheetOfParticipant",
          action.data.isRefreshList ? null : PrevSelectedTimesheetItem
        )
        .set("selectedTimeSheetComments", null)
        .set("workerTimeSheetsList", action.data.isRefreshList ? [] : prevList);
    }
    case ACTIONS.WORKER_TIMESHEETS.SUCCESS: {
      const prevList = state.get("workerTimeSheetsList") || [];
      const isSearch = state.get("workerTimeSheetsListSearch") || false;

      let newWorkerTimeSheetsList = [];

      if (isSearch) {
        newWorkerTimeSheetsList = [...action?.data?.data];
      } else {
        const newData = action?.data?.data.filter(
          (item) =>
            !prevList.some((existingItem) => existingItem.id === item.id)
        );

        if (newData.length > 0) {
          newWorkerTimeSheetsList = [...prevList, ...newData];
        } else {
          newWorkerTimeSheetsList = [...action?.data?.data];
        }
      }

      return state
        .set("workerTimesheetLoader", false)
        .set("workerTimesheetError", "")
        .set("workerTimesheetPaginationLoader", false)
        .set("workerTimeSheetsListPagination", getPaginationObj(action?.data))
        .set("workerTimeSheetsList", newWorkerTimeSheetsList);
    }

    // case ACTIONS.WORKER_TIMESHEETS.SUCCESS: {
    //   const prevList = state.get("workerTimeSheetsList") || [];
    //   const isSearch = state.get("workerTimeSheetsListSearch") || false;
    //   console.log({isSearch})

    //   let newWorkerTimeSheetsList = [];
    //   if(isSearch){
    //     newWorkerTimeSheetsList = [...action?.data?.data];
    //   }else{
    //     newWorkerTimeSheetsList = [
    //       ...prevList,
    //       ...action?.data?.data.filter(item => !prevList.some(existingItem => existingItem.id === item.id)),
    //     ];
    //   }
    // //  [...action?.data?.data]
    // //     : [...prevList, ...action?.data?.data];
    //   return state
    //     .set("workerTimesheetLoader", false)
    //     .set("workerTimesheetError", "")
    //     .set("workerTimesheetPaginationLoader", false)
    //     .set("workerTimeSheetsListPagination", getPaginationObj(action?.data))
    //     .set("workerTimeSheetsList", newWorkerTimeSheetsList);
    // }
    case ACTIONS.WORKER_TIMESHEETS.ERROR: {
      return state
        .set("workerTimesheetLoader", false)
        .set("workerTimesheetPaginationLoader", false)
        .set("workerTimesheetError", action.error);
    }

    case ACTIONS.WORKER_TIMESHEETS_LOCALLY_SEARCH: {
      return state.set("workerTimeSheetsListSearch", action.search);
    }

    case ACTIONS.RESET_WORKER_TIMESHEETS: {
      return state
        .set("workerTimeSheetsListPagination", emptyPaginationObj)
        .set("workerTimeSheetsList", []);
    }

    case ACTIONS.SET_SELECTED_TIMESHEET_LOCALLY: {
      return state
        .set("selectedTimesheetOfParticipant", action.data)
        .set("isNextSelectedTimeSheetOfParticipant", null);
    }

    case ACTIONS.TIMESHEET_COMMENT_CREATED.PENDING: {
      return state
        .set("timesheetCommentCreateLoader", true)
        .set("timesheetCommentCreateError", "");
    }
    case ACTIONS.TIMESHEET_COMMENT_CREATED.SUCCESS: {
      const prevSelectedTimesheetOfParticipant =
        state.get("selectedTimesheetOfParticipant") || null;
      if (prevSelectedTimesheetOfParticipant) {
        Object.assign(prevSelectedTimesheetOfParticipant, {
          comment: action.data.text,
        });
      }
      const prevComments = state.get("selectedTimeSheetComments") || {
        ...emptyPaginationObj,
        data: [],
      };
      if (prevComments && prevComments?.data && action?.data) {
        prevComments.data.push({ ...action.data });
      }
      return state
        .set("timesheetCommentCreateLoader", false)
        .set("timesheetCommentCreateError", "")
        .set("selectedTimesheetOfParticipant", {
          ...prevSelectedTimesheetOfParticipant,
        })
        .set("selectedTimeSheetComments", { ...prevComments });
    }
    case ACTIONS.TIMESHEET_COMMENT_CREATED.ERROR: {
      return state
        .set("timesheetCommentCreateLoader", false)
        .set("timesheetCommentCreateError", action.error);
    }

    case ACTIONS.TIMESHEET_COMMENTS_FETCHED.PENDING: {
      const prevComments = state.get("selectedTimeSheetComments") || {
        ...emptyPaginationObj,
        data: [],
      };
      return state
        .set("timesheetCommentsListLoader", true)
        .set("timesheetCommentsListError", "")
        .set(
          "selectedTimeSheetComments",
          parseBooleanVal(action.isFirstCall) ? null : prevComments
        );
    }
    case ACTIONS.TIMESHEET_COMMENTS_FETCHED.SUCCESS: {
      const prevComments = state.get("selectedTimeSheetComments") || {
        ...emptyPaginationObj,
        data: [],
      };
      const obj = _.cloneDeep(action?.data || {});
      if (prevComments && prevComments?.data && action?.data) {
        Object.assign(obj, {
          data: [...prevComments.data, ...action?.data?.data],
        });
      }
      return state
        .set("timesheetCommentsListLoader", false)
        .set("timesheetCommentsListError", "")
        .set("selectedTimeSheetComments", { ...obj });
    }
    case ACTIONS.TIMESHEET_COMMENTS_FETCHED.ERROR: {
      return state
        .set("timesheetCommentsListLoader", false)
        .set("timesheetCommentsListError", action.error);
    }

    case ACTIONS.TIMESHEET_DATA_UPDATED.PENDING: {
      return state
        .set("timesheetupdateLoader", true)
        .set("timesheetupdateError", "");
    }
    case ACTIONS.TIMESHEET_DATA_UPDATED.SUCCESS: {
      // const selectedTimeSheet = state.get('selectedTimesheetOfParticipant') || null;
      let isNextTimesheet = null;
      const prevWorkerTimeSheetsList =
        state.get("workerTimeSheetsList") || null;
      const prevSelectedTimesheetOfParticipant =
        state.get("selectedTimesheetOfParticipant") || null;
      if (prevSelectedTimesheetOfParticipant) {
        Object.assign(prevSelectedTimesheetOfParticipant, {
          ...prevSelectedTimesheetOfParticipant,
          ...action.data,
        });
      }
      if (prevWorkerTimeSheetsList && action?.uuid) {
        const parentIndex = prevWorkerTimeSheetsList.findIndex(
          (x) => x.uuid === action.uuid.parent_uuid
        );
        if (
          parentIndex !== -1 &&
          prevWorkerTimeSheetsList[parentIndex]?.timeSheet &&
          action?.uuid?.timeSheetUuid
        ) {
          const timeSheetIndex = prevWorkerTimeSheetsList[
            parentIndex
          ].timeSheet.findIndex((x) => x.uuid === action.uuid.timeSheetUuid);
          if (timeSheetIndex !== -1) {
            prevWorkerTimeSheetsList[parentIndex]?.timeSheet.splice(
              timeSheetIndex,
              1,
              { ...action.data }
            );
          }

          if (action.isNext) {
            const isAnyPendingExist = prevWorkerTimeSheetsList?.[
              parentIndex
            ]?.timeSheet?.filter((x) => x.status === TIMESHEET_STATUS.PENDING);
            // console.log("isAnyPendingExist", isAnyPendingExist);
            if (isAnyPendingExist.length > 0) {
              isNextTimesheet = {
                ...isAnyPendingExist?.[0],
                worker_id: prevWorkerTimeSheetsList?.[parentIndex]?.id,
                parent_uuid: prevWorkerTimeSheetsList?.[parentIndex]?.uuid,
                worker_name: `${prevWorkerTimeSheetsList?.[parentIndex]?.first_name} ${prevWorkerTimeSheetsList?.[parentIndex]?.last_name}`,
              };
            } else {
              isNextTimesheet = { id: -1 };
            }
          }
        }
      }

      return state
        .set("timesheetupdateLoader", false)
        .set("timesheetupdateError", "")
        .set(
          "selectedTimesheetOfParticipant",
          isNextTimesheet?.id
            ? null
            : {
              ...prevSelectedTimesheetOfParticipant,
            }
        )
        .set(
          "isNextSelectedTimeSheetOfParticipant",
          isNextTimesheet?.id ? { ...isNextTimesheet } : null
        )
        .set("workerTimeSheetsList", [...prevWorkerTimeSheetsList]);
    }
    case ACTIONS.TIMESHEET_DATA_UPDATED.ERROR: {
      return state
        .set("timesheetupdateLoader", false)
        .set("timesheetupdateError", action.error);
    }

    case ACTIONS.TIMESHEET_STATUS_CHANGED.PENDING: {
      return state
        .set("timesheetStatusChangeLoader", {
          ...action.data.data,
          isNext: action.data.isNext,
        })
        .set("timesheetStatusChangeError", "");
    }
    case ACTIONS.TIMESHEET_STATUS_CHANGED.SUCCESS: {
      const statusChangeNavigate =
        state.get("timesheetStatusChangeLoader") || null;
      const prevWorkerTimeSheetsList =
        state.get("workerTimeSheetsList") || null;
      const prevSelectedTimesheetOfParticipant =
        state.get("selectedTimesheetOfParticipant") || null;
      let isAnyPendingExist = [];

      //Worker Timesheet List find and update status changed for ( single & multiple timesheets base on approveAll check)
      if (!action.data.hasOwnProperty("approveAll")) {
        if (
          prevSelectedTimesheetOfParticipant &&
          prevWorkerTimeSheetsList &&
          action?.data?.data?.uuid[0]
        ) {
          const parentIndex = prevWorkerTimeSheetsList.findIndex(
            (x) => x.uuid === prevSelectedTimesheetOfParticipant.parent_uuid
          );
          if (
            parentIndex !== -1 &&
            prevWorkerTimeSheetsList[parentIndex]?.timeSheet &&
            action?.data?.data?.uuid[0]
          ) {
            const timeSheetIndex = prevWorkerTimeSheetsList[
              parentIndex
            ].timeSheet.findIndex(
              (x) => x.uuid === action?.data?.data?.uuid[0]
            );
            const filterPendingArrayClone = _.cloneDeep(
              prevWorkerTimeSheetsList[parentIndex]?.timeSheet
            );

            if (timeSheetIndex !== -1) {
              prevWorkerTimeSheetsList[parentIndex]?.timeSheet.splice(
                timeSheetIndex,
                1,
                {
                  ...prevWorkerTimeSheetsList[parentIndex]?.timeSheet[
                  timeSheetIndex
                  ],
                  status: action?.data?.data?.status,
                }
              );
            }

            // If approve and Next is True
            isAnyPendingExist = filterPendingArrayClone.filter(
              (x) => x.status === TIMESHEET_STATUS.PENDING
            );
            if (
              isAnyPendingExist.length > 1 &&
              statusChangeNavigate !== null &&
              statusChangeNavigate.isNext === true
            ) {
              const length = isAnyPendingExist.length;
              const currentCursorIndex = isAnyPendingExist.findIndex(
                (x) => x.uuid === prevSelectedTimesheetOfParticipant.uuid
              );
              if (currentCursorIndex !== -1) {
                Object.assign(prevSelectedTimesheetOfParticipant, {
                  ...isAnyPendingExist[
                  currentCursorIndex === length - 1
                    ? 0
                    : currentCursorIndex + 1
                  ],
                });
              }
            }
          }
        }
        //Selected Participant Timesheet status changed
        if (
          statusChangeNavigate !== null &&
          statusChangeNavigate.isNext === false &&
          prevSelectedTimesheetOfParticipant &&
          action?.data?.data &&
          prevSelectedTimesheetOfParticipant.uuid ===
          action?.data?.data?.uuid[0]
        ) {
          Object.assign(prevSelectedTimesheetOfParticipant, {
            status: action.data.data.status,
          });
        }
      }

      if (
        action.data.hasOwnProperty("approveAll") &&
        action.data?.data?.approvalSheets
      ) {
        const { approvalSheets, status } = action.data?.data;
        approvalSheets?.forEach((sheet) => {
          const parentIndex = prevWorkerTimeSheetsList.findIndex(
            (x) => x.uuid === sheet.parent_uuid
          );
          if (
            parentIndex !== -1 &&
            prevWorkerTimeSheetsList[parentIndex]?.timeSheet
          ) {
            const timeSheetIndex = prevWorkerTimeSheetsList[
              parentIndex
            ].timeSheet.findIndex((x) => x.uuid === sheet.uuid);
            if (timeSheetIndex !== -1) {
              prevWorkerTimeSheetsList[parentIndex]?.timeSheet.splice(
                timeSheetIndex,
                1,
                {
                  ...prevWorkerTimeSheetsList[parentIndex]?.timeSheet[
                  timeSheetIndex
                  ],
                  status: status,
                }
              );
            }
          }
        });
      }

      return state
        .set("timesheetStatusChangeLoader", null)
        .set("timesheetStatusChangeError", "")
        .set("workerTimeSheetsList", [...prevWorkerTimeSheetsList])
        .set(
          "isAnyPendingExist",
          action.data.hasOwnProperty("approveAll")
            ? null
            : isAnyPendingExist.length > 1 ||
              (statusChangeNavigate !== null &&
                statusChangeNavigate.isNext === false)
              ? true
              : "false"
        )
        .set(
          "selectedTimesheetOfParticipant",
          action.data.hasOwnProperty("approveAll")
            ? null
            : isAnyPendingExist.length > 1 ||
              (statusChangeNavigate !== null &&
                statusChangeNavigate.isNext === false)
              ? {
                ...prevSelectedTimesheetOfParticipant,
              }
              : null
        );
    }
    case ACTIONS.TIMESHEET_STATUS_CHANGED.ERROR: {
      return state
        .set("timesheetStatusChangeLoader", null)
        .set("timesheetStatusChangeError", action.error);
    }

    case ACTIONS.RESET_IS_ANY_PENDING_EXIST_FOR_WORKER: {
      return state.set("isAnyPendingExist", null);
    }

    case ACTIONS.WORKER_TIMESHEET_EXPORT_LIST.PENDING: {
      return state
        .set("workerTimeSheetExportListLoader", true)
        .set("workerTimeSheetExportListError", "")
        .set("workerTimeSheetExportList", null);
    }
    case ACTIONS.WORKER_TIMESHEET_EXPORT_LIST.SUCCESS: {
      return state
        .set("workerTimeSheetExportListLoader", false)
        .set("workerTimeSheetExportListError", "")
        .set("workerTimeSheetExportList", action.data);
    }
    case ACTIONS.WORKER_TIMESHEET_EXPORT_LIST.ERROR: {
      return state
        .set("workerTimeSheetExportListLoader", false)
        .set("workerTimeSheetExportListError", action.error);
    }

    case ACTIONS.SINGLE_TIMESHEET_DETAILS.PENDING: {
      return state
        .set("singleTimesheetDetailsLoader", true)
        .set("singleTimesheetDetailsError", "")
        .set("singleTimesheetDetails", null);
    }
    case ACTIONS.SINGLE_TIMESHEET_DETAILS.SUCCESS: {
      return state
        .set("singleTimesheetDetailsLoader", false)
        .set("singleTimesheetDetailsError", "")
        .set("singleTimesheetDetails", action.data);
    }
    case ACTIONS.SINGLE_TIMESHEET_DETAILS.ERROR: {
      return state
        .set("singleTimesheetDetailsLoader", false)
        .set("singleTimesheetDetailsError", action.error);
    }

    case ACTIONS.EXPORT_TIMESHEET_TO_PAYROLL.PENDING: {
      return state
        .set("exportTimesheetToPayrollLoader", true)
        .set("exportTimesheetToPayrollError", "")
        .set("singleTimesheetDetails", null);
    }
    case ACTIONS.EXPORT_TIMESHEET_TO_PAYROLL.SUCCESS: {
      const payrollType = action.data.type;
      const payload = action?.data?.employees;
      const prevWorkerTimeSheetExportList =
        state.get("workerTimeSheetExportList") || null;
      let updatedList = state.get("workerTimeSheetExportList") || null;
      if (
        prevWorkerTimeSheetExportList &&
        action?.data &&
        action.data?.employees?.length > 0
      ) {
        updatedList = prevWorkerTimeSheetExportList.map((parent) => {
          const payloadItem = payload.find((item) => item.uuid === parent.uuid);
          if (payloadItem) {
            const exportStatusKey = `${payrollType}_export_status`;
            parent.timeSheet = parent.timeSheet.map((child) => ({
              ...child,
              export_status: payloadItem.timesheets.includes(child.uuid)
                ? action?.data[`${exportStatusKey}`] ||
                PAYROLL_EXPORT_STATUS.INITIATED
                : child[`${exportStatusKey}`],
              [`${exportStatusKey}`]: payloadItem.timesheets.includes(
                child.uuid
              )
                ? action?.data[`${exportStatusKey}`] ||
                PAYROLL_EXPORT_STATUS.INITIATED
                : child[`${exportStatusKey}`],
            }));
          }
          return parent;
        });
      }
      return state
        .set("exportTimesheetToPayrollLoader", false)
        .set("exportTimesheetToPayrollError", "")
        .set("workerTimeSheetExportList", [...updatedList]);
    }
    case ACTIONS.EXPORT_TIMESHEET_TO_PAYROLL.ERROR: {
      return state
        .set("exportTimesheetToPayrollLoader", false)
        .set("exportTimesheetToPayrollError", action.error);
    }

    case ACTIONS.TIMESHEET_STATUS_CHANGED_LOCALLY: {
      const payload = action?.data?.employees;
      const prevWorkerTimeSheetExportList =
        state.get("workerTimeSheetExportList") || null;
      let updatedList = state.get("workerTimeSheetExportList") || null;
      if (
        prevWorkerTimeSheetExportList &&
        action?.data &&
        action.data?.employees?.length > 0
      ) {
        updatedList = prevWorkerTimeSheetExportList.map((parent) => {
          const payloadItem = payload.find(
            (item) => item.p_uuid === parent.uuid
          );

          if (payloadItem) {
            parent.timeSheet = parent.timeSheet.map((child) => ({
              ...child,
              progress: payloadItem.children.includes(child.uuid)
                ? action?.data?.status
                : child.progress,
            }));
          }
          return parent;
        });
      }
      return state.set("workerTimeSheetExportList", [...updatedList]);
    }

    case ACTIONS.PAYROLL_EXPORT_CALLBACK: {
      const payrollType = action.data.type;
      const prevWorkerTimeSheetExportList =
        state.get("workerTimeSheetExportList") || null;
      if (
        prevWorkerTimeSheetExportList &&
        prevWorkerTimeSheetExportList.length > 0 &&
        action.data &&
        action.data.worker_uuid
      ) {
        const workerIndex = prevWorkerTimeSheetExportList.findIndex(
          (x) => x.uuid === action.data.worker_uuid
        );
        if (workerIndex !== -1) {
          const payrollTimeSheetsMsg = {};
          if (payrollType) {
            Object.assign(payrollTimeSheetsMsg, {
              [`${payrollType}_time_sheets`]: {
                type: payrollType,
                message: action.data?.message,
                [`${payrollType}_status`]:
                  action.data.status === true ? "success" : "error",
              },
            });
          }
          const payrollTypeKey = `${payrollType}_export_status`;
          prevWorkerTimeSheetExportList.splice(workerIndex, 1, {
            ...prevWorkerTimeSheetExportList[workerIndex],
            timeSheet: prevWorkerTimeSheetExportList[
              workerIndex
            ]?.timeSheet?.map((item) => ({
              ...item,
              export_status:
                item[`${payrollTypeKey}`] === PAYROLL_EXPORT_STATUS.INITIATED
                  ? action.data.status === true
                    ? PAYROLL_EXPORT_STATUS.SUCCESS
                    : PAYROLL_EXPORT_STATUS.ERROR
                  : item[`${payrollTypeKey}`],
              [`${payrollTypeKey}`]:
                item[`${payrollTypeKey}`] === PAYROLL_EXPORT_STATUS.INITIATED
                  ? action.data.status === true
                    ? PAYROLL_EXPORT_STATUS.SUCCESS
                    : PAYROLL_EXPORT_STATUS.ERROR
                  : item[`${payrollTypeKey}`],
            })),
            ...payrollTimeSheetsMsg,
          });
        }
      }

      return state.set(
        "workerTimeSheetExportList",
        prevWorkerTimeSheetExportList
          ? [...prevWorkerTimeSheetExportList]
          : null
      );
    }

    case ACTIONS.AUTHENTICATED_PAYROLL_UPDATE_LOCALLY: {
      return state.set("isAuthenticateForPayroll", action.data);
    }

    case ACTIONS.SHOW_RECURRING_SHIFT_MODAL: {
      return state.set("showRecurringModal", action.payload);
    }
    case ACTIONS.GET_ROSTER_INVOICE.PENDING: {
      return state
        .setIn(["rosterInvoices", "rosterInvoicesLoading"], true)
        .setIn(["rosterInvoices", "error"], "");
    }
    case ACTIONS.GET_ROSTER_INVOICE.SUCCESS: {
      return state
        .setIn(["rosterInvoices", "rosterInvoicesLoading"], false)
        .setIn(["rosterInvoices", "rosterInvoicesList"], action.data);
    }
    case ACTIONS.GET_ROSTER_INVOICE.ERROR: {
      return state
        .setIn(["rosterInvoices", "rosterInvoicesLoading"], false)
        .setIn(["rosterInvoices", "error"], action.error);
    }

    case ACTIONS.UPDATE_CALENDER_SEARCH_LOCALLY: {
      return state.set("searchtextOfScheduleShifts", action.data);
    }
    case ACTIONS.GET_PUBLIC_HOLIDAYS_LIST.PENDING: {
      return state
        .set("publicHolidaysListLoader", true)
        .set("publicHolidaysListError", "")
        .set("publicHolidaysList", null);
    }
    case ACTIONS.GET_PUBLIC_HOLIDAYS_LIST.SUCCESS: {
      return state
        .set("publicHolidaysListLoader", false)
        .set("publicHolidaysListError", "")
        .set("publicHolidaysList", action.data);
    }
    case ACTIONS.GET_PUBLIC_HOLIDAYS_LIST.ERROR: {
      return state
        .set("publicHolidaysListLoader", false)
        .set("publicHolidaysListError", action.error);
    }

    case ACTIONS.GET_TIMESHEET_OF_WORKERS_REQUEST: {
      return state.set("getTimesheetOfWorkersRequest", action.payload);
    }
    case ACTIONS.GET_TIMESHEET_OF_WORKERS.PENDING: {
      const requestPage = state.get("getTimesheetOfWorkersRequest")?.page ?? 1;
      const keepExistingList = Number(requestPage) > 1;
      return state
        .set("getTimesheetOfWorkersLoader", true)
        .set("getTimesheetOfWorkersError", "")
        .set(
          "getTimesheetOfWorkersList",
          keepExistingList ? state.get("getTimesheetOfWorkersList") : null
        );
    }
    case ACTIONS.GET_TIMESHEET_OF_WORKERS.SUCCESS: {
      return state
        .set("getTimesheetOfWorkersLoader", false)
        .set("getTimesheetOfWorkersError", "")
        .set("getTimesheetOfWorkersList", action.data);
    }
    case ACTIONS.GET_TIMESHEET_OF_WORKERS.ERROR: {
      return state
        .set("getTimesheetOfWorkersLoader", false)
        .set("getTimesheetOfWorkersError", action.error);
    }
    case ACTIONS.SELECTED_TIMESHEET_DATE_RANGE_CHANGED: {
      return state.set("selectedTimesheetDateRange", action.payload);
    }
    case ACTIONS.RESET_SELECTED_TIMESHEET_DATE_RANGE: {
      return state.set("selectedTimesheetDateRange", null);
    }
    case ACTIONS.SINGLE_TIMESHEET_DETAILS_CALL: {
      return state.set("singleTimesheetDetailsCall", action.payload);
    }
    case ACTIONS.SINGLE_WORKER_SELECTED_FOR_TIMESHEET_DETAIL_CALL: {
      const prevReq = state.get("singleWorkerTimesheetLastRequest");
      const nextReq = action.payload;
      const prevCtx = timesheetListFetchContextKey(prevReq);
      const nextCtx = timesheetListFetchContextKey(nextReq);
      const contextChanged = prevCtx !== nextCtx;
      return state
        .set("singleWorkerTimesheetLastRequest", nextReq)
        .set("singleWorkerSelectedForTimesheetDetailLoader", true)
        .set("singleWorkerSelectedForTimesheetDetailError", "")
        .set(
          "singleWorkerSelectedForTimesheetDetail",
          contextChanged
            ? null
            : state.get("singleWorkerSelectedForTimesheetDetail")
        );
    }
    case ACTIONS.SINGLE_WORKER_SELECTED_FOR_TIMESHEET_DETAIL.PENDING: {
      return state
        .set("singleWorkerSelectedForTimesheetDetailLoader", true)
        .set("singleWorkerSelectedForTimesheetDetailError", "");
    }
    case ACTIONS.SINGLE_WORKER_SELECTED_FOR_TIMESHEET_DETAIL.SUCCESS: {
      return state
        .set("singleWorkerSelectedForTimesheetDetailLoader", false)
        .set("singleWorkerSelectedForTimesheetDetailError", "")
        .set("singleWorkerSelectedForTimesheetDetail", action.data);
    }
    case ACTIONS.SINGLE_WORKER_SELECTED_FOR_TIMESHEET_DETAIL.ERROR: {
      return state
        .set("singleWorkerSelectedForTimesheetDetailLoader", false)
        .set("singleWorkerSelectedForTimesheetDetailError", action.error);
    }
    case ACTIONS.CLICKED_WORKER_TIMESHEET_SIDEBAR: {
      return state.set("singleClickedWorkerTimesheetInSidebar", action.payload);
    }
    case ACTIONS.SINGLE_CHECKBOX_SELECTED_IN_TIMESHEET: {
      return state.set("singleCheckboxTimesheetSelected", action.payload);
    }
    case ACTIONS.RESET_SINGLE_CHECKBOX_SELECTED_IN_TIMESHEET: {
      return state.set("singleCheckboxTimesheetSelected", null);
    }
    case ACTIONS.RESET_UPDATED_TIMESHEET_PAGE: {
      return state
        .set("getTimesheetOfWorkersRequest", null)
        .set("getTimesheetOfWorkersLoader", false)
        .set("getTimesheetOfWorkersError", "")
        .set("getTimesheetOfWorkersList", null)
        .set("selectedTimesheetDateRange", null)
        .set("singleTimesheetDetailsCall", null)
        .set("singleWorkerTimesheetLastRequest", null)
        .set("singleWorkerSelectedForTimesheetDetailLoader", false)
        .set("singleWorkerSelectedForTimesheetDetailError", "")
        .set("singleWorkerSelectedForTimesheetDetail", null)
        .set("singleClickedWorkerTimesheetInSidebar", null)
        .set("singleCheckboxTimesheetSelected", null);
    }
    case ACTIONS.PATCH_WORKER_TIMESHEET_ROW: {
      const detail = state.get("singleWorkerSelectedForTimesheetDetail");
      const rows = detail?.timesheets?.data;
      const uuid = action.payload?.uuid;
      if (!detail || !Array.isArray(rows) || !uuid) {
        return state;
      }

      const { uuid: _patchUuid, ...fields } = action.payload;
      const updatedData = rows.map((row) =>
        row.uuid === uuid ? { ...row, ...fields } : row
      );

      let nextState = state.set("singleWorkerSelectedForTimesheetDetail", {
        ...detail,
        timesheets: {
          ...detail.timesheets,
          data: updatedData,
        },
      });

      const selected = state.get("singleCheckboxTimesheetSelected");
      if (selected?.uuid === uuid) {
        const patchedRow = updatedData.find((row) => row.uuid === uuid);
        if (patchedRow) {
          nextState = nextState.set("singleCheckboxTimesheetSelected", patchedRow);
        }
      }

      return nextState;
    }
    default: {
      return state;
    }
  }
}
